import React, { useEffect, useState, useCallback, useRef } from "react";
import { StyleSheet, View, Platform } from "react-native";
import useToast from "@/hooks/useToast";
import useNavigationGuard from "@/hooks/useNavigationGuard";
import useAuth from "@/hooks/useAuth";
import { TableView } from "@/components/Table";
import { ActionBar, type ActionBarAction } from "@/components/ActionBar";
import { TableDrawer } from "@/components/Table/Drawer";
import { useDirtyRows } from "@/hooks/useDirtyRows";
import { useFlyoutFromParams } from "@/hooks/useFlyoutFromParams";
import { useRouter } from "expo-router";
import { Query, Order, LinkedRecordConfig, makeDefaultQuery } from "@/components/Table/types";
import { Toggle, ToggleProps } from "@/components/ui/Toggle";
import { TableViewProps } from "./Table/props";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useRowSelectionContext } from "@/providers/RowSelectionProvider";
import { CSVDownloadModal } from "@/components/Modals/CSVDownloadModal";
import { ModalCard } from "@/components/Modals/ModalCard";
import { useModal } from "@/hooks/useModal";
import { useIsFocused, useFocusEffect } from "@react-navigation/native";
import { useBadge } from "@/hooks/useBadge";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useEntityTablePageWriter } from "@/providers/EntityTablePageProvider";
import * as Device from "expo-device";

/** Format entity name: replace underscores with spaces and title case */
function formatEntityName(name: string): string {
  return name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export interface SaveConfirmationConfig<T> {
  // Check if any patches should trigger confirmation
  shouldConfirm: (patches: any[]) => boolean;
  // Storage key for "don't show again" preference
  storageKey: string;
  // Render the confirmation modal
  renderModal: (props: {
    patches: any[];
    onConfirm: (extraData?: any) => void;
    onCancel: () => void;
  }) => React.ReactNode;
  // Optional callback when save is confirmed with extra data from modal
  onConfirmWithData?: (patches: any[], extraData: any) => Promise<void>;
  // Optional function to get default extra data when modal is skipped
  getDefaultExtraData?: (patches: any[]) => Promise<any>;
}

export interface DeleteConfirmationConfig<T> {
  // Check if deletion should trigger confirmation (always true for now, but allows future flexibility)
  shouldConfirm?: (ids: string[], selectedMap: Record<string, T>) => boolean;
  // Storage key for "don't show again" preference
  storageKey: string;
  // Render the confirmation modal
  renderModal: (props: {
    ids: string[];
    selectedMap: Record<string, T>;
    count: number;
    onConfirm: () => void;
    onCancel: () => void;
  }) => React.ReactNode;
}

interface EntityTablePageProps<T> {
  entityName: string;
  entityNamePlural?: string;
  // Data fetching
  // Optional: a page may instead supply data via useCustomData (guarded internally).
  fetchData?: () => Promise<T[]>;
  // Services return the saved rows; the component ignores the resolved value.
  updateEntities?: (patches: any[]) => Promise<unknown>;
  deleteEntities?: (ids: string[]) => Promise<void | boolean>;

  // Table configuration
  tableConfig: {
    ignore?: string[];
    headerActions?: TableViewProps["headerActions"];
    summary?: TableViewProps["summary"];
    readonly?: (string | (string | ((key: string, value: any, row: any) => any))[])[];
    validators?: Record<string, (r: any) => boolean>;
    cellTextCallback?: (value: any, key: string, row: any) => string;
    headerFunc?: (header: string) => string;
    // Config builders widen literals (e.g. `type: "array"`) via conditional spreads,
    // so accept a loose record here — consistent with the rest of tableConfig's typing.
    dataTypes?: Record<string, any>;
    writeKeys?: TableViewProps["writeKeys"];
  };

  // Custom action handler (optional - defaults to flyout behavior)
  onAction?: (row: T, buttonRef?: React.RefObject<any>) => void;

  // Custom New button handler (optional - defaults to opening new flyout panel)
  onNew?: (selection: { selectedIds: string[]; selectedMap: Record<string, any>; count: number }) => void;

  // Filter configuration
  // Config builders declare these with `as const`, so accept readonly arrays.
  filterChoices: readonly {
    name: string;
    // `(string & {})` keeps autocomplete for the known kinds while tolerating the widened
    // `string` that config builders produce from heterogeneous `as const` filter arrays.
    kind:
      | "text"
      | "number"
      | "boolean"
      | "date"
      | "decimal"
      | "enum"
      | "linked_record"
      | "array"
      | "iso_week"
      | (string & {});
    menus?: readonly ("filter" | "visible" | "orderby" | "groupby")[];
    displayName?: string;
    options?: readonly { value: string; color?: string }[];
    linkedRecord?: LinkedRecordConfig;
  }[];

  // Optional features
  enableCreate?: boolean;
  enableDelete?: boolean;
  enableSave?: boolean;
  newRequiresSelect?: boolean;
  toggleProps?: Omit<ToggleProps, "buttonProps" | "style">;

  rowNavIcon?: keyof typeof MaterialCommunityIcons.glyphMap;
  // storageKey?: string;

  // Custom data hook (optional)
  useCustomData?: () => { data: T[]; loading: boolean; refresh: () => void };

  // Save confirmation (optional)
  saveConfirmation?: SaveConfirmationConfig<T>;

  // Delete confirmation (optional)
  deleteConfirmation?: DeleteConfirmationConfig<T>;

  // Custom actions for ActionBar right side (can be static or a function receiving selection state)
  customActions?:
    | ActionBarAction[]
    | ((context: { selectedIds: string[]; selectedMap: Record<string, T>; count: number }) => ActionBarAction[]);

  // Selection-dependent actions for ActionBar left/center (next to selection indicator)
  selectionActions?:
    | ActionBarAction[]
    | ((context: { selectedIds: string[]; selectedMap: Record<string, T>; count: number }) => ActionBarAction[]);

  // Archive: controlled from parent
  showArchived?: boolean;
  onArchiveToggle?: (showArchived: boolean) => void;

  // Archive/unarchive entities
  archiveEntities?: (ids: string[], archived: boolean) => Promise<void>;

  // Initial query state (e.g. restored from a cache)
  initialQuery?: Query<string>;
  // Called whenever the active query changes
  onActiveQueryChange?: (query: Query<string>) => void;
}

export function EntityTablePage<T>({
  entityName,
  entityNamePlural = `${entityName}s`,
  fetchData,
  updateEntities,
  deleteEntities,
  tableConfig,
  filterChoices,
  enableCreate = false,
  enableDelete = false,
  enableSave = false,
  newRequiresSelect = false,
  // storageKey = "entity_flyout",
  useCustomData,
  toggleProps,
  rowNavIcon = "exit-to-app",
  onAction: customOnAction,
  onNew: customOnNew,
  saveConfirmation,
  deleteConfirmation,
  customActions,
  selectionActions,
  showArchived = false,
  onArchiveToggle,
  archiveEntities,
  initialQuery,
  onActiveQueryChange,
}: EntityTablePageProps<T>) {
  // const id = useResolvedId("id");
  const toast = useToast();
  const router = useRouter();
  const { displayNames } = useAuth();
  const { openModal, closeModal } = useModal();
  const { refreshBadge } = useBadge();

  const [data, setData] = useState<T[]>([]);
  const isFocused = useIsFocused();
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeQuery, _setActiveQuery] = useState<Query<string>>(initialQuery ?? makeDefaultQuery());
  const setActiveQuery = useCallback(
    (update: Query<string> | ((prev: Query<string>) => Query<string>)) => {
      _setActiveQuery((prev) => {
        const next = typeof update === "function" ? update(prev) : update;
        onActiveQueryChange?.(next);
        return next;
      });
    },
    [onActiveQueryChange],
  );

  // Reset query and search state when the entity view changes (e.g. toggling between views)
  const prevEntityRef = useRef(entityNamePlural);
  useEffect(() => {
    if (prevEntityRef.current !== entityNamePlural) {
      prevEntityRef.current = entityNamePlural;
      const restored = initialQuery ?? makeDefaultQuery();
      _setActiveQuery(restored);
      onActiveQueryChange?.(restored);
      setSearch("");
    }
  }, [entityNamePlural, initialQuery, onActiveQueryChange]);

  const handleOrderByChange = useCallback((orderby?: Order<string>) => {
    setActiveQuery((prev) => ({ ...prev, orderby }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleHideColumn = useCallback((columnKey: string) => {
    setActiveQuery((prev) => {
      const visible = prev.visible ? [...prev.visible] : [];
      const existing = visible.findIndex((v) => v.name === columnKey);
      if (existing >= 0) {
        visible[existing] = { ...visible[existing], visible: false };
      } else {
        visible.push({ name: columnKey, visible: false });
      }
      return { ...prev, visible };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Use custom data hook if provided, otherwise use internal state
  const customData = useCustomData?.();
  const finalData = customData?.data ?? data;
  const finalLoading = customData?.loading ?? loading;

  // Stable refresh reference using ref pattern
  const refreshRef = useRef<() => void>(() => {});
  const stableRefresh = useCallback(() => {
    refreshRef.current();
  }, []);
  const finalRefresh = customData?.refresh ?? stableRefresh;

  // Selection - use context for entity-based selection
  const rowSelectionContext = useRowSelectionContext();
  const selectedMap = rowSelectionContext.getEntitySelection(entityName.toLowerCase());
  const selectedIds = Object.keys(selectedMap);
  const count = selectedIds.length;

  // Dirty map
  const { dirtyMap, isDirty, handleCellBlur, setDirtyMap, discardAll } = useDirtyRows<T>();
  const { setPreventNavigation } = useNavigationGuard();

  // Table reset key for clearing selection externally
  const [tableResetKey, setTableResetKey] = useState(0);

  // Sync table reset when context selection is cleared externally
  const prevCountRef = useRef(count);
  useEffect(() => {
    if (prevCountRef.current > 0 && count === 0) {
      setTableResetKey((k) => k + 1);
    }
    prevCountRef.current = count;
  }, [count]);

  // Track filtered data stages from TableView for CSV export
  const filteredDataRef = useRef<{ filteredData: any[]; filteredAndSearchedData: any[]; currentPageData: any[] }>({
    filteredData: [],
    filteredAndSearchedData: [],
    currentPageData: [],
  });
  const handleFilteredDataChange = useCallback(
    (data: { filteredData: any[]; filteredAndSearchedData: any[]; currentPageData: any[] }) => {
      filteredDataRef.current = data;
    },
    [],
  );

  // Flyout routed by ?id or ?new
  const flyout = useFlyoutFromParams("panel");

  // Header integration — on web desktop, push ActionBar config into the app header
  const entityTablePage = useEntityTablePageWriter();
  const isWebDesktop = Platform.OS === "web" && Device.deviceType !== Device.DeviceType.PHONE;

  useEffect(() => setPreventNavigation(isDirty), [isDirty, setPreventNavigation]);

  const refresh = async () => {
    if (!fetchData) return;
    setLoading(true);
    try {
      const result = await fetchData();
      setData(result);
    } catch (err: any) {
      toast.showToast({
        title: `Failed to fetch ${entityNamePlural.toLowerCase()}`,
        message: err.message,
        toastType: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Keep ref updated with latest refresh function
  refreshRef.current = refresh;

  useEffect(() => {
    if (!useCustomData) {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Register for global refresh events
  useEffect(() => {
    rowSelectionContext.registerRefresh(entityName.toLowerCase(), finalRefresh);
    return () => {
      rowSelectionContext.unregisterRefresh(entityName.toLowerCase(), finalRefresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityName, finalRefresh]);

  const handleAction = (row: T, buttonRef?: React.RefObject<any>) => {
    // Use custom handler if provided, otherwise use default flyout behavior
    if (customOnAction) {
      customOnAction(row, buttonRef);
      return;
    }

    // Default behavior: open flyout panel
    if (!row || typeof row !== "object" || !("id" in row)) {
      console.error("Invalid row data - missing id property", row);
      return;
    }
    router.setParams({ panel: entityName.toLowerCase(), id: (row as { id: string }).id, new: undefined });
  };

  // Stable callback for row selection to prevent re-render loops
  const handleSelect = useCallback(
    (selectedRows: Record<string, any>) => {
      rowSelectionContext.setEntitySelection(entityName.toLowerCase(), selectedRows);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entityName, rowSelectionContext.setEntitySelection],
  );

  const executeDelete = async () => {
    try {
      await deleteEntities!(selectedIds);
      finalRefresh();
      toast.showToast({
        title: `Deleted ${selectedIds.length} ${entityNamePlural.toLowerCase()}`,
        toastType: "success",
      });
    } catch (err: any) {
      toast.showToast({
        title: `Failed to delete ${entityNamePlural.toLowerCase()}`,
        message: err.message,
        toastType: "error",
      });
    }
  };

  const handleDelete = async () => {
    if (!deleteEntities || finalLoading || selectedIds.length === 0) return;

    // Check if confirmation is needed
    if (deleteConfirmation) {
      const shouldConfirm = deleteConfirmation.shouldConfirm
        ? deleteConfirmation.shouldConfirm(selectedIds, selectedMap)
        : true;

      if (shouldConfirm) {
        // Check "don't show again" preference
        const skipConfirmation = await AsyncStorage.getItem(deleteConfirmation.storageKey);

        if (skipConfirmation !== "true") {
          // Show confirmation modal
          openModal(
            deleteConfirmation.renderModal({
              ids: selectedIds,
              selectedMap,
              count: selectedIds.length,
              onConfirm: async () => {
                closeModal();
                await executeDelete();
              },
              onCancel: closeModal,
            }),
          );
          return;
        }
      }
    }

    // Direct delete if no confirmation needed
    await executeDelete();
  };

  const performSave = async (patches: any[], extraData?: any) => {
    try {
      // If there's extra data and a handler for it, call that instead
      if (extraData && saveConfirmation?.onConfirmWithData) {
        await saveConfirmation.onConfirmWithData(patches, extraData);
      } else {
        await updateEntities!(patches);
      }
      toast.showToast({ title: "Changes saved", toastType: "success" });
      setDirtyMap({});
      finalRefresh();
      refreshBadge?.(entityNamePlural.toLowerCase());
    } catch (err: any) {
      toast.showToast({ title: "Failed to save", message: err.message, toastType: "error" });
    }
  };

  const handleSave = async () => {
    if (!updateEntities) return;
    const patches = Object.entries(dirtyMap).map(([id, fields]) => ({ id, ...fields }));
    if (patches.length === 0) return;

    // Check if save confirmation is needed
    if (saveConfirmation && saveConfirmation.shouldConfirm(patches)) {
      // Check "don't show again" preference
      const skipConfirmation = await AsyncStorage.getItem(saveConfirmation.storageKey);
      if (skipConfirmation === "true") {
        // Skip modal, use default extra data if available
        const defaultExtraData = saveConfirmation.getDefaultExtraData
          ? await saveConfirmation.getDefaultExtraData(patches)
          : undefined;
        await performSave(patches, defaultExtraData);
        return;
      }

      // Show confirmation modal
      openModal(
        saveConfirmation.renderModal({
          patches,
          onConfirm: async (extraData) => {
            closeModal();
            await performSave(patches, extraData);
          },
          onCancel: closeModal,
        }),
      );
      return;
    }

    // No confirmation needed, just save
    await performSave(patches);
  };

  const handleNew = () => {
    // Use custom handler if provided, otherwise use default flyout behavior
    if (customOnNew) {
      customOnNew({ selectedIds, selectedMap, count });
      return;
    }

    // Default behavior: open new flyout panel
    if (!enableCreate) return;
    router.setParams({ new: "true", panel: entityName.toLowerCase() });
    flyout.setVisible(true);
  };

  const handleExport = () => {
    const allColumns = Object.keys(finalData[0] || {})
      .filter((key) => !tableConfig.ignore?.includes(key))
      .filter((key) => !activeQuery?.visible?.some((item) => item.name === key && item.visible === false));

    const { filteredData, filteredAndSearchedData, currentPageData } = filteredDataRef.current;
    const selectedDataArr = Object.values(selectedMap);

    openModal(
      <ModalCard>
        <CSVDownloadModal
          data={finalData}
          filteredData={filteredData}
          searchedData={filteredAndSearchedData}
          currentPageData={currentPageData}
          selectedData={selectedDataArr}
          allColumns={allColumns}
          headerFunc={tableConfig.headerFunc}
          override={undefined}
          baseFilename={entityName.toLowerCase()}
          onClose={closeModal}
        />
      </ModalCard>,
    );
  };

  // ─── Header integration (web desktop only) ───
  const toggleNode = toggleProps ? (
    <Toggle
      {...toggleProps}
      textStyle={{ fontSize: 12 }}
      buttonProps={{ style: { paddingHorizontal: 8, borderWidth: 0, backgroundColor: "transparent" } }}
    />
  ) : null;

  const searchConfig = {
    placeholder: `Search ${displayNames?.[entityNamePlural.toLowerCase()]?.plural ?? entityNamePlural}`,
    value: search,
    onChangeText: setSearch,
  };

  const handleArchive = async (archived: boolean) => {
    if (!archiveEntities || selectedIds.length === 0) return;
    const count = selectedIds.length;
    const idsToArchive = [...selectedIds];
    setData((prev) => prev.filter((row: any) => !idsToArchive.includes(row.id)));
    rowSelectionContext.clearEntitySelection(entityName.toLowerCase());
    setTableResetKey((k) => k + 1);
    try {
      await archiveEntities(idsToArchive, archived);
      toast.showToast({
        title: `${archived ? "Archived" : "Unarchived"} ${count} ${count === 1 ? entityName.toLowerCase() : entityNamePlural.toLowerCase()}`,
        toastType: "success",
      });
    } catch (err: any) {
      toast.showToast({
        title: `Failed to ${archived ? "archive" : "unarchive"}`,
        message: err.message,
        toastType: "error",
      });
      finalRefresh();
    }
  };

  const selectionConfig = {
    count,
    actions: [
      ...(typeof selectionActions === "function"
        ? selectionActions({ selectedIds, selectedMap: selectedMap as Record<string, T>, count })
        : (selectionActions ?? [])),
      ...(archiveEntities
        ? [
            {
              id: showArchived ? "unarchive" : "archive",
              label: showArchived ? "Unarchive" : "Archive",
              icon: (showArchived ? "archive-off" : "archive") as ActionBarAction["icon"],
              onPress: () => handleArchive(!showArchived),
            },
          ]
        : []),
      ...(enableDelete && deleteEntities
        ? [
            {
              id: "delete",
              label: "Delete",
              icon: "trash-can-outline" as const,
              variant: "danger" as const,
              onPress: handleDelete,
            },
          ]
        : []),
    ],
    onClear: () => {
      rowSelectionContext.clearEntitySelection(entityName.toLowerCase());
      setTableResetKey((k) => k + 1);
    },
  };

  const dirtyConfig =
    enableSave && isDirty
      ? { count: Object.keys(dirtyMap).length, onSave: handleSave, onDiscard: discardAll }
      : undefined;

  const refreshHandler = () => {
    finalRefresh();
    setTableResetKey((k) => k + 1);
  };

  const actionsConfig: ActionBarAction[] = [
    ...(typeof customActions === "function"
      ? customActions({ selectedIds, selectedMap: selectedMap as Record<string, T>, count })
      : (customActions ?? [])),
    ...(onArchiveToggle
      ? [
          {
            id: "archive-toggle",
            label: showArchived ? "Hide Archived" : "Show Archived",
            icon: (showArchived ? "archive-off" : "archive") as ActionBarAction["icon"],
            onPress: () => onArchiveToggle(!showArchived),
          },
        ]
      : []),
    { id: "export", label: "Export", icon: "download" as const, onPress: handleExport },
    ...(enableCreate
      ? [
          {
            id: "new",
            label: `New ${displayNames?.[entityNamePlural.toLowerCase()]?.name ?? formatEntityName(entityName)}`,
            icon: "plus" as const,
            variant: "primary" as const,
            disabled: finalLoading || (newRequiresSelect && count === 0),
            onPress: handleNew,
          },
        ]
      : []),
  ];

  // Keep header in sync on every render (web desktop only)
  useEffect(() => {
    if (!isWebDesktop || !isFocused) return;
    entityTablePage.register({
      toggleNode,
      search: searchConfig,
      selection: selectionConfig,
      onRefresh: refreshHandler,
      dirty: dirtyConfig,
      actions: actionsConfig,
    });
  });

  // Clear header when this screen loses focus or unmounts
  useFocusEffect(
    useCallback(() => {
      // on focus: no-op (the useEffect above handles registration)
      return () => {
        if (isWebDesktop) entityTablePage.unregister();
      };
    }, [isWebDesktop, entityTablePage]),
  );

  return (
    <View style={styles.container}>
      <TableDrawer
        filterChoices={filterChoices as unknown as React.ComponentProps<typeof TableDrawer>["filterChoices"]}
        page={entityNamePlural.toLowerCase()}
        onQueryChange={setActiveQuery}
        externalQuery={activeQuery}
      />
      <View style={styles.view}>
        {/* On web desktop the ActionBar lives in the app header via context; on mobile render it inline */}
        {!isWebDesktop && (
          <View style={styles.actionBarContainer}>
            {toggleNode}
            <ActionBar
              style={{ flex: 1 }}
              search={searchConfig}
              selection={selectionConfig}
              dirty={dirtyConfig}
              onRefresh={refreshHandler}
              actions={actionsConfig}
            />
          </View>
        )}

        {isFocused ? (
          <TableView
            key={`${entityNamePlural.toLowerCase()}-table`}
            style={styles.table}
            headerActions={tableConfig.headerActions}
            summary={tableConfig.summary}
            query={activeQuery}
            data={finalData}
            ignore={tableConfig.ignore}
            search={search}
            searchHidesRow={true}
            readonly={tableConfig.readonly as React.ComponentProps<typeof TableView>["readonly"]}
            canEdit={!showArchived}
            onAction={handleAction}
            cellTextCallback={tableConfig.cellTextCallback}
            validateCell={tableConfig.validators as any}
            rowNavIcon={rowNavIcon}
            loading={finalLoading}
            onSelect={handleSelect}
            onCellBlur={handleCellBlur}
            headerFunc={tableConfig.headerFunc}
            storageKey={entityName.toLowerCase()}
            dataTypes={tableConfig.dataTypes}
            writeKeys={tableConfig.writeKeys}
            onFilteredDataChange={handleFilteredDataChange}
            onOrderByChange={handleOrderByChange}
            onHideColumn={handleHideColumn}
            resetKey={tableResetKey}
          />
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, flexDirection: "row" },
  view: { flex: 1, flexDirection: "column", padding: 16, marginLeft: 56 },
  actionBarContainer: { flexDirection: "row", alignItems: "center", gap: 16, marginBottom: 8 },
  table: { marginTop: 4, width: "100%", height: "100%" },
});
